import { FC } from "react";

import { Box } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";

import { AutomatedCommunicationCard } from "../components/AutomatedCommunicationCard";
import { SendAutomatedEmailTestButton } from "../components/SendAutomatedEmailTestButton";
import { AutomatedCommunicationsFormData, RowKey } from "../types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ExtraWysiwygVariable } from "@/components/OutlinedTextFieldWysiwyg/type";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { Translations } from "@/public/@types/translations";

type DashboardKey = keyof Translations["dashboard"] & string;

type FulfillmentKind = "ready-for-pickup" | "order-shipped";

interface FulfillmentEmailCardProps {
  kind: FulfillmentKind;
  openRow: RowKey | null;
  onToggleOpen: (row: RowKey) => void;
  defaultContent: string;
}

const KIND_CONFIG: Record<
  FulfillmentKind,
  {
    fieldKey: "readyForPickup" | "orderShipped";
    label: DashboardKey;
    chipLabel: DashboardKey;
    chipTooltip: DashboardKey;
    amplitudeEmailType: string;
    variableKeys: ("orderDetails" | "pickupAddress" | "pickupInstructions")[];
    dataTestPrefix: string;
    testEmailType: "fulfillment-ready-for-pickup" | "fulfillment-order-shipped";
  }
> = {
  "ready-for-pickup": {
    fieldKey: "readyForPickup",
    label: "liveFormEditor.fulfillmentEmail.readyForPickup.label",
    chipLabel: "liveFormEditor.fulfillmentEmail.readyForPickup.chipLabel",
    chipTooltip: "liveFormEditor.fulfillmentEmail.readyForPickup.chipTooltip",
    amplitudeEmailType: "Order Ready For Pickup",
    variableKeys: ["orderDetails", "pickupAddress", "pickupInstructions"],
    dataTestPrefix: "live-form-editor-ready-for-pickup",
    testEmailType: "fulfillment-ready-for-pickup",
  },
  "order-shipped": {
    fieldKey: "orderShipped",
    label: "liveFormEditor.fulfillmentEmail.orderShipped.label",
    chipLabel: "liveFormEditor.fulfillmentEmail.orderShipped.chipLabel",
    chipTooltip: "liveFormEditor.fulfillmentEmail.orderShipped.chipTooltip",
    amplitudeEmailType: "Order Shipped",
    variableKeys: ["orderDetails"],
    dataTestPrefix: "live-form-editor-order-shipped",
    testEmailType: "fulfillment-order-shipped",
  },
};

const VARIABLE_TAGS: Record<string, { tag: string; labelKey: DashboardKey }> = {
  orderDetails: {
    tag: "{{Order details}}",
    labelKey: "liveFormEditor.fulfillmentEmail.variable.orderDetails",
  },
  pickupAddress: {
    tag: "{{Pickup address}}",
    labelKey: "liveFormEditor.fulfillmentEmail.variable.pickupAddress",
  },
  pickupInstructions: {
    tag: "{{Pickup instructions}}",
    labelKey: "liveFormEditor.fulfillmentEmail.variable.pickupInstructions",
  },
};

export const FulfillmentEmailCard: FC<FulfillmentEmailCardProps> = ({
  kind,
  openRow,
  onToggleOpen,
  defaultContent,
}) => {
  const { t } = useTranslate();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const form = useFormContext<AutomatedCommunicationsFormData>();
  const config = KIND_CONFIG[kind];

  const enabled = useWatch({ control: form.control, name: `${config.fieldKey}.enabled` });

  const extraVariables: ExtraWysiwygVariable[] = config.variableKeys.map((key) => ({
    tag: VARIABLE_TAGS[key].tag,
    label: t("dashboard", VARIABLE_TAGS[key].labelKey),
  }));

  return (
    <AutomatedCommunicationCard<AutomatedCommunicationsFormData>
      label={t("dashboard", config.label)}
      control={form.control}
      errors={form.formState.errors}
      dataEmailType={kind}
      collapse={{ isOpen: openRow === kind, onToggle: () => onToggleOpen(kind) }}
      fields={{ subject: `${config.fieldKey}.subject`, content: `${config.fieldKey}.content` }}
      enabled={Boolean(enabled)}
      onEnabledChange={(next) => {
        form.setValue(`${config.fieldKey}.enabled`, next, { shouldDirty: true });
        logUserActionEvent(next ? AmplitudeEvents.AutomaticEmailEnabled : AmplitudeEvents.AutomaticEmailDisabled, {
          email_type: config.amplitudeEmailType,
        });
      }}
      chip={{
        label: t("dashboard", config.chipLabel),
        showWhen: "enabled",
        tooltip: t("dashboard", config.chipTooltip),
      }}
      body={{ defaultValue: defaultContent, extraVariables }}
      onBlur={() =>
        logUserActionEvent(AmplitudeEvents.AutomaticEmailCustomized, { email_type: config.amplitudeEmailType })
      }
      dataTestPrefix={config.dataTestPrefix}
      footnote={
        <Box marginTop={2} display="flex" justifyContent="flex-start">
          <SendAutomatedEmailTestButton<AutomatedCommunicationsFormData>
            emailType={config.testEmailType}
            subjectFieldName={`${config.fieldKey}.subject`}
            contentFieldName={`${config.fieldKey}.content`}
          />
        </Box>
      }
    />
  );
};
